import React, { useState } from 'react';
import { ActivityIndicator, Linking, ScrollView, StyleSheet, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { AppText } from '../AppText';
import FilledButton from '../FilledButton';
import MustLogin from '../pantry/MustLogin';
import { useSite } from './useSite';
import { SiteMapHeader } from './SiteMapHeader';
import { BookingModeBadge } from './BookingModeBadge';
import { EventSlotPickerModal } from './EventSlotPickerModal';
import {
  compareByOpensAt,
  deviceTimeZone,
  eventDisplayName,
  formatEventWindow,
  isValidTimeZone,
  timeZoneAbbreviation,
} from './siteFormatting';
import Colors from '../../constants/Colors';
import { useTranslation } from '../../providers/TranslationProvider';
import { isEmbedded, openEmbeddedLoginUrl } from '../../utils/embeddedLogin';
import { logError } from '../../utils/logger';
import { Address, BookingMode, Coordinate, Event, Site, Window } from '../../types/Site';

const styles = StyleSheet.create({
  container: { flex: 1 },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  content: { padding: 16 },
  sectionHeading: { fontWeight: '700', fontSize: 18, color: Colors.primaryColor, marginBottom: 16 },
  eventList: { gap: 14 },
  // Borrowed from yp-site-list-styling's SiteStatusPill "closed" variant, which is what that
  // branch's list view shows for a site with no upcoming window -- ports the look (not the
  // open/closed status logic itself, which needs useNow/computeSiteStatus infrastructure this
  // branch doesn't have) for the details page's own no-events case.
  noUpcomingPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    backgroundColor: Colors.grayBorder,
  },
  noUpcomingDot: {
    width: 9,
    height: 9,
    borderRadius: 5,
    backgroundColor: Colors.siteListClosedDot,
  },
  noUpcomingText: { fontSize: 16, fontWeight: '700', color: Colors.veryDarkGray },
  eventCard: {
    borderWidth: 1,
    borderColor: Colors.lightGray,
    borderRadius: 8,
    padding: 18,
  },
  eventName: { fontWeight: '700', fontSize: 17, color: Colors.veryDarkGray },
  windowText: { fontSize: 15, color: Colors.veryDarkGray, marginTop: 6 },
  moreWindows: { fontSize: 13, color: Colors.grayInfoText, fontStyle: 'italic', marginTop: 4 },
  cta: { marginTop: 16 },
  timezoneNote: { fontSize: 12, color: Colors.grayInfoText, marginBottom: 12 },
  contactSection: {
    marginTop: 24,
    paddingTop: 20,
    borderTopWidth: 1,
    borderTopColor: Colors.grayBorder,
    gap: 16,
  },
  contactRow: { flexDirection: 'row', gap: 14, alignItems: 'flex-start' },
  contactText: { flexShrink: 1, fontSize: 15, color: Colors.primaryColor, textDecorationLine: 'underline' },
});

export interface SiteDetailsViewProps {
  siteId: string;
  isLoggedIn: boolean;
  onBack: () => void;
  // The neighbor's resolved location, used to compute a client-side distance -- see useSite.ts's
  // distanceMiles and the TODO on ForNeighborsApi.ts's describeSite.
  userCoordinate?: Coordinate;
}

// Styled details for a FreshTrak site (or any site without a legacy pantry record) -- legacy
// plentifulProvider/imported sites go to the existing PantryDetails component instead, since they
// have a real record there (see resolveSiteDetailsDestination). Matches the "pantry-2a" design
// handoff, minus the booking sheet/booked-state work that needs new API support -- see
// docs/superpowers/specs/2026-08-03-site-details-view-styling-design.md.
// Used both as SiteDetailsScreen's full-screen content (narrow layouts, reached via
// navigateToSiteDetails) and embedded inline in SiteMapPantriesContent's wide layout.
function formatAddress(address: Address | undefined): string | null {
  if (!address) return null;
  const parts = [address.street1, address.street2, address.city, address.state, address.zipCode].filter(
    Boolean
  );
  return parts.length ? parts.join(', ') : null;
}

const CTA_TRANSLATION_KEY: Partial<Record<BookingMode, string>> = {
  reservation: 'site_details_reserve_cta',
  preRegistration: 'site_details_plan_visit_cta',
};

interface EventOccurrence {
  event: Event;
  slot: Window;
  // hasMoreWindows is per-event (the API truncated that event's window list), but tiles are now
  // per-window -- attach the "+ more" note only to the chronologically-last tile for its event
  // rather than repeating it on every occurrence.
  isLastWindowForEvent: boolean;
}

// One tile per window/occurrenceId, in day/time order, across all of a site's events.
function flattenOccurrences(events: Event[]): EventOccurrence[] {
  return events
    .flatMap((event) => {
      // windows is required per the Event type, but this is still-stabilizing API data -- fall
      // back to an empty list rather than throwing if a real response omits it.
      const windows = [...(event.windows ?? [])].sort(compareByOpensAt);
      return windows.map((slot, index) => ({
        event,
        slot,
        isLastWindowForEvent: index === windows.length - 1,
      }));
    })
    .sort((a, b) => compareByOpensAt(a.slot, b.slot));
}

interface EventCardProps extends EventOccurrence {
  timezone?: string;
  onOpenSlotPicker: (occurrence: EventOccurrence) => void;
}

function EventCard({ event, slot, isLastWindowForEvent, timezone, onOpenSlotPicker }: EventCardProps) {
  const { t } = useTranslation();
  const ctaKey = CTA_TRANSLATION_KEY[event.bookingMode];

  return (
    <View style={styles.eventCard} testID={`event-card-${event.eventId}-${slot.occurrenceId}`}>
      <AppText style={styles.eventName}>
        {eventDisplayName(event, t('site_details_event_name_fallback'))}
      </AppText>
      <BookingModeBadge bookingMode={event.bookingMode} />
      <AppText style={styles.windowText}>{formatEventWindow(slot, timezone)}</AppText>
      {event.hasMoreWindows && isLastWindowForEvent && (
        <AppText style={styles.moreWindows}>{t('site_details_more_windows')}</AppText>
      )}
      {ctaKey && (
        <View style={styles.cta}>
          <FilledButton
            testID={`event-cta-${event.eventId}-${slot.occurrenceId}`}
            onPress={() => onOpenSlotPicker({ event, slot, isLastWindowForEvent })}
            label={t(ctaKey)}
            color={Colors.primaryColor}
            full
            height={48}
            radius={24}
          />
        </View>
      )}
    </View>
  );
}

async function openContactLink(url: string, siteId: string) {
  try {
    await Linking.openURL(url);
  } catch (e) {
    logError(e as Error, { siteId });
  }
}

function ContactSection({ site, address }: { site: Site; address: string | null }) {
  const websiteUrl =
    site.website && (site.website.startsWith('http') ? site.website : `https://${site.website}`);

  return (
    <View style={styles.contactSection}>
      {address && (
        <View style={styles.contactRow}>
          <MaterialIcons name="location-on" size={24} color={Colors.primaryColor} />
          <AppText
            style={styles.contactText}
            onPress={() =>
              openContactLink(
                `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`,
                site.siteId
              )
            }
          >
            {address}
          </AppText>
        </View>
      )}
      {site.phone && (
        <View style={styles.contactRow}>
          <MaterialIcons name="phone" size={24} color={Colors.primaryColor} />
          <AppText
            style={styles.contactText}
            onPress={() => openContactLink(`tel:${site.phone}`, site.siteId)}
          >
            {site.phone}
          </AppText>
        </View>
      )}
      {site.website && (
        <View style={styles.contactRow}>
          <MaterialIcons name="public" size={24} color={Colors.primaryColor} />
          <AppText
            style={styles.contactText}
            onPress={() => openContactLink(websiteUrl as string, site.siteId)}
          >
            {site.website}
          </AppText>
        </View>
      )}
    </View>
  );
}

function SiteDetailsContent({
  site,
  onBack,
  distanceMiles,
  isLoggedIn,
}: {
  site: Site;
  onBack: () => void;
  distanceMiles: number | null;
  isLoggedIn: boolean;
}) {
  const { t, translation } = useTranslation();
  const navigation = useNavigation();
  const address = formatAddress(site.address);
  // events is required per the Site type, but this is still-stabilizing API data -- fall back to
  // an empty list rather than throwing if a real response omits it, mirroring SiteListItem.tsx.
  const occurrences = flattenOccurrences(site.events ?? []);
  const { timezone } = site;
  // Only worth calling out when it's a well-formed zone that actually differs from the
  // neighbor's own device zone -- otherwise the note is just noise. Matches
  // yp-site-list-styling's SiteListItem.tsx.
  const showTimezoneNote =
    timezone !== undefined && isValidTimeZone(timezone) && timezone !== deviceTimeZone();
  const [openOccurrence, setOpenOccurrence] = useState<EventOccurrence | null>(null);
  const [showMustLogin, setShowMustLogin] = useState(false);

  const handleOpenSlotPicker = (occurrence: EventOccurrence) => {
    if (!isLoggedIn) {
      setShowMustLogin(true);
      setOpenOccurrence(null);
      return;
    }
    setOpenOccurrence(occurrence);
    setShowMustLogin(false);
  };

  // Mirrors PantryDetails.jsx's goToLogin: an embedded partner widget must break out to the real
  // app for login rather than rendering the registration flow inside the iframe. goBack() isn't
  // carried over from legacy's goToLogin -- that dismisses PantryDetails' own modal state, which
  // SiteDetailsView doesn't have an equivalent of.
  const goToLogin = () => {
    setShowMustLogin(false);
    if (isEmbedded()) {
      openEmbeddedLoginUrl();
      return;
    }
    navigation.navigate('Language');
  };

  return (
    <ScrollView style={styles.container} testID="site-details-screen">
      <SiteMapHeader site={site} onBack={onBack} distanceMiles={distanceMiles} />
      <View style={styles.content}>
        <AppText style={styles.sectionHeading}>{t('site_details_upcoming_events_heading')}</AppText>
        {showTimezoneNote && timezone && (
          <AppText style={styles.timezoneNote}>
            {t('site_details_timezone_note', { zone: timeZoneAbbreviation(timezone, new Date()) })}
          </AppText>
        )}
        {occurrences.length > 0 ? (
          <View style={styles.eventList}>
            {occurrences.map((occurrence) => (
              <EventCard
                key={`${occurrence.event.eventId}-${occurrence.slot.occurrenceId}`}
                {...occurrence}
                timezone={timezone}
                onOpenSlotPicker={handleOpenSlotPicker}
              />
            ))}
          </View>
        ) : (
          <View style={styles.noUpcomingPill} testID="no-upcoming-services-pill">
            <View style={styles.noUpcomingDot} />
            <AppText style={styles.noUpcomingText}>{t('site_details_no_upcoming_events')}</AppText>
          </View>
        )}
        <ContactSection site={site} address={address} />
      </View>
      {openOccurrence && (
        <EventSlotPickerModal
          key={openOccurrence.slot.occurrenceId}
          occurrenceId={openOccurrence.slot.occurrenceId}
          eventName={eventDisplayName(openOccurrence.event, t('site_details_event_name_fallback'))}
          window={openOccurrence.slot}
          timezone={timezone}
          onClose={() => setOpenOccurrence(null)}
        />
      )}
      {showMustLogin && (
        <MustLogin translation={translation} onOk={goToLogin} onCancel={() => setShowMustLogin(false)} />
      )}
    </ScrollView>
  );
}

export function SiteDetailsView({ siteId, isLoggedIn, onBack, userCoordinate }: SiteDetailsViewProps) {
  const { site, loading, error, distanceMiles } = useSite(siteId, isLoggedIn, userCoordinate);

  if (loading) {
    return (
      <View style={styles.loadingContainer} testID="site-details-screen">
        <ActivityIndicator size="large" color={Colors.primaryColor} testID="site-details-loading" />
      </View>
    );
  }

  if (error || !site) {
    return (
      <View style={styles.container} testID="site-details-screen">
        <AppText>Unable to load site details.</AppText>
      </View>
    );
  }

  return (
    <SiteDetailsContent site={site} onBack={onBack} distanceMiles={distanceMiles} isLoggedIn={isLoggedIn} />
  );
}
